/// <amd-module name="DRITools/DRIToolsHub/Callgraph/CallgraphSlowTraceDialog" />
import * as React from "react";
import d3 = require("d3");

// VSS
import { ObservableValue } from "VSS/Core/Observable";

// VSSUI
import { Accordion, IAccordionSection } from "VSSUI/Accordion";
import { CustomDialog } from "VSSUI/Dialog";
import { ContentSize } from "VSSUI/Callout";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { PanelContent } from "VSSUI/Panel";

// DRITools
import { parseTrace, TraceMethod } from "DRITools/Core/Helpers/TraceParser";
import { Obj } from "DRITools/Core/Contracts/DRITools";
import * as Resources from "DRITools/DRIToolsHub/Resources";

interface Wrapping {
    [key: string]: Method;
}

interface Method {
    elements: (HTMLTableRowElement | null)[];
    parentId: string | null;
}

export interface ICallgraphSlowTraceDialog {
    slowTraces: Obj[];
    onDismiss: () => void;
}

export class CallgraphSlowTraceDialog extends React.Component<ICallgraphSlowTraceDialog> {
    public render(): JSX.Element {
        const parsedTraces: TraceMethod[] = this.parseTraces(this.props.slowTraces);

        let sections: IAccordionSection[] = [];
        parsedTraces.forEach((trace, index) => {
            sections.push({
                isCollapsed: new ObservableValue(parsedTraces.length !== 1),
                key: "accordion-item-slow-trace" + index,
                onRenderContent: () => <ParserTab traceMethod={trace} />,
                title: trace.name
            });
        });

        return (
            <CustomDialog
                modal={true}
                onDismiss={this.props.onDismiss}
                contentSize={ContentSize.Auto}
            >
                <CustomHeader className="bolt-header-with-commandbar" separator>
                    <HeaderTitleArea>
                        <div className="title-m">{Resources.Callgraph_SlowTrace}</div>
                    </HeaderTitleArea>
                </CustomHeader>
                <PanelContent>
                    <Accordion
                        className="trace-parse"
                        sections={sections}
                        onSectionClicked={this.onSectionClicked}
                    />
                </PanelContent>
            </CustomDialog>
        );
    }

    private onSectionClicked = (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        section: IAccordionSection
    ): void => {
        const collapsed = section.isCollapsed as ObservableValue<boolean>;
        collapsed.value = !collapsed.value;
    };

    private parseTraces = (slowTraces: Obj[]): TraceMethod[] => {
        let message = "";

        slowTraces.forEach(trace => {
            message += trace.Message.toString();
        });

        return parseTrace(message).roots;
    };
}

export interface IParserTabProps {
    traceMethod: TraceMethod;
}

class ParserTab extends React.Component<IParserTabProps> {
    private wrapping: Wrapping = {};

    public render(): JSX.Element {
        const { traceMethod } = this.props;
        return (
            <table>
                <thead>
                    <tr className="monospaced-s">
                        <th className="table-time">{Resources.Callgraph_Time}</th>
                        <th className="table-inc">{Resources.Callgraph_Inc}</th>
                        <th>{Resources.Callgraph_Method}</th>
                    </tr>
                </thead>
                <tbody>
                    {traceMethod.children.map((child: TraceMethod) => {
                        return this.constructNode(child);
                    })}
                </tbody>
            </table>
        );
    }

    private constructNode(
        node: TraceMethod,
        tabCount: number = 0,
        parentId: string | null = null
    ): (any[] | JSX.Element | null)[] | JSX.Element {
        const thisId: string = this.newGuid();
        if (node.isSproc) {
            return (
                <tr className="monospaced-s" key={thisId}>
                    <td>{node.start}</td>
                    <td>{":"}</td>
                    <td>
                        <span
                            style={
                                { paddingLeft: tabCount * 10 + 8 + "px" } as React.CSSProperties
                            }
                        >
                            {node.name + node.data[0]}
                        </span>
                    </td>
                </tr>
            );
        } else {
            this.wrapping[thisId] = {
                elements: [],
                parentId: parentId
            };
            return [
                <tr
                    className="monospaced-s"
                    key={thisId}
                    ref={row => this.wrapping[thisId].elements.push(row)}
                    onMouseEnter={(event: React.MouseEvent<HTMLTableRowElement>) =>
                        this.handleMouseEnter(event, thisId)
                    }
                    onMouseLeave={event => this.handleMouseOut(event, thisId)}
                >
                    <td>{node.start}</td>
                    <td>{node.end !== -1 ? ":" : ":unbal"}</td>
                    <td>
                        <span style={{ paddingLeft: tabCount++ * 10 + (node.end !== -1 ? 8 : 0) + "px" }}>
                            {node.name}
                        </span>
                    </td>
                </tr>,
                node.children.map((child: TraceMethod) => {
                    return this.constructNode(child, tabCount, thisId);
                }),
                node.end !== -1 ? (
                    <tr
                        className="monospaced-s"
                        key={"-" + thisId}
                        ref={row => this.wrapping[thisId].elements.push(row)}
                        onMouseEnter={(event: React.MouseEvent<HTMLTableRowElement>) =>
                            this.handleMouseEnter(event, thisId)
                        }
                        onMouseLeave={event => this.handleMouseOut(event, thisId)}
                    >
                        <td>{node.start}</td>
                        <td>{":" + node.end}</td>
                        <td>
                            <span
                                style={
                                    {
                                        paddingLeft: --tabCount * 10 + 8 + "px"
                                    } as React.CSSProperties
                                }
                            >
                                \{node.name}
                            </span>
                        </td>
                    </tr>
                ) : null
            ];
        }
    }

    private handleMouseEnter(
        event: React.MouseEvent<HTMLTableRowElement>,
        className: string
    ): void {
        const node: Method = this.wrapping[className];
        node.elements.forEach((element: HTMLTableRowElement) => {
            d3.select(element).style("background", "#dedede");
        });
        if (node.parentId) {
            this.wrapping[node.parentId].elements.forEach(
                (element: HTMLTableRowElement) => {
                    d3.select(element).style("background", "#a6a6a6");
                }
            );
        }
    }

    private handleMouseOut(
        event: React.MouseEvent<HTMLTableRowElement>,
        className: string
    ): void {
        const node: Method = this.wrapping[className];
        node.elements.forEach((element: HTMLTableRowElement) => {
            d3.select(element).style("background", "none");
        });
        if (node.parentId) {
            this.wrapping[node.parentId].elements.forEach(
                (element: HTMLTableRowElement) => {
                    d3.select(element).style("background", "none");
                }
            );
        }
    }

    private newGuid(): string {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}
